import { memo, useCallback, useMemo, useState, type ReactNode } from "react";
import { Box, Button, Checkbox, Grid, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Tooltip, Typography } from "@mui/material";
import { Block, KeyboardArrowLeft, KeyboardArrowRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from "@mui/icons-material";
import { List as VirtualList, type RowComponentProps } from "react-window";

// Twin column genérico — extraído de CompanyTransferList (usado em
// UserForm pra vincular empresa) pra não duplicar a mesma UI de novo em
// cada tela que precisa vincular muitos itens de uma vez (mover
// tudo/filtrar/mover em lote), como o vínculo de colaboradores da Escala.
// CompanyTransferList continua existindo como wrapper fino em cima deste,
// preservando a interface (usableCompanies/values/setValues) que UserForm
// já usa.
interface TransferListProps<T> {
  items: T[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  getSecondaryLabel?: (item: T) => string | undefined;
  // Quando devolve uma string pro item, ele fica travado — não entra no
  // checked, não move de coluna, e mostra a razão num Tooltip com um ícone
  // de bloqueio. Undefined/"" deixa o item normal. Vale só na coluna da
  // esquerda: quem já está na direita sempre pode sair, mesmo que hoje o
  // motivo do bloqueio se aplique a ele. Usado pela trava de exclusividade
  // do vínculo de colaboradores da Escala (docs/escala/01-dominio.md) — sem
  // isso o único aviso era o erro do backend depois de tentar salvar.
  getDisabledReason?: (item: T) => string | undefined;
  leftTitle?: string;
  rightTitle?: string;
  itemNoun?: string;
  searchPlaceholder?: string;
  // Esconde o TextField de busca embutido — pra quando o filtro por nome já
  // mora em outro lugar (o FilterModal do vínculo de colaboradores da
  // Escala, por exemplo) e ter os dois juntos seria filtro duplicado.
  // `items` continua sendo a fonte da verdade: quem esconde a busca própria
  // assume filtrar por nome antes de passar `items` pra cá.
  hideSearch?: boolean;
  onMoveRight?: () => void;
  onMoveLeft?: () => void;
  // Controle extra por item, só na coluna da direita (já vinculado) — o
  // seletor de turma A/B do vínculo de colaboradores da Escala (R30), sem
  // precisar de uma seção separada pra uma escolha binária simples. Renderiza
  // como secondaryAction do ListItem (fora da área de clique do
  // ListItemButton), então não interfere no toggle/mover do item.
  renderRightExtra?: (item: T) => ReactNode;
}

// Altura de linha e de lista fixas — react-window (já usado em
// ConsultaProdutoPage.tsx, mesmo padrão de número fixo em vez de
// percentual) precisa saber o tamanho de cada linha pra calcular só o que
// está visível, sem montar as outras. Altura percentual ("100%") faria
// react-window depender de ResizeObserver pra medir o container de verdade
// — indisponível no jsdom dos testes (e não garantido em todo navegador),
// travando com ReferenceError. Número fixo dispensa essa medição.
const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 56;
const COLUMN_HEIGHT = 400;
const LIST_AREA_HEIGHT = COLUMN_HEIGHT - HEADER_HEIGHT;

interface TransferRowProps<T> {
  visiveis: T[];
  isRight: boolean;
  checkedSet: Set<string>;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  getSecondaryLabel?: (item: T) => string | undefined;
  getDisabledReason?: (item: T) => string | undefined;
  renderRightExtra?: (item: T) => ReactNode;
  onToggle: (id: string) => void;
}

// Linha da lista virtualizada. react-window só monta as linhas dentro (+
// margem de scroll) da janela visível — sem isso, uma loja com centenas de
// colaboradores criava centenas de ListItemButton/Checkbox do MUI de uma vez
// (cada um com custo de sx/ripple nada trivial), o que travava tanto a
// abertura do modal quanto a rolagem, e piorava conforme a loja crescesse.
// Fica memoizada também: clicar um checkbox só muda `checkedSet`, então o
// memo evita re-renderizar quem não mudou dentro da janela visível.
function TransferListRowInner<T>({
  index,
  style,
  visiveis,
  isRight,
  checkedSet,
  getId,
  getLabel,
  getSecondaryLabel,
  getDisabledReason,
  renderRightExtra,
  onToggle,
}: RowComponentProps<TransferRowProps<T>>) {
  const item = visiveis[index];
  if (!item) return null;

  const id = getId(item);
  const labelId = `transfer-list-item-${id}-label`;
  const disabledReason = isRight ? undefined : getDisabledReason?.(item);
  const extra = isRight ? renderRightExtra?.(item) : undefined;
  const row = (
    <ListItem disablePadding secondaryAction={extra} sx={{ height: "100%" }}>
      <ListItemButton role="listitem" onClick={() => onToggle(id)} disabled={!!disabledReason}>
        <ListItemIcon>
          <Checkbox
            checked={checkedSet.has(id)}
            tabIndex={-1}
            disableRipple
            disabled={!!disabledReason}
            slotProps={{ input: { "aria-labelledby": labelId } }}
          />
        </ListItemIcon>
        <ListItemText
          id={labelId}
          title={getLabel(item)}
          primary={getLabel(item)}
          secondary={disabledReason ?? getSecondaryLabel?.(item)}
          // Nome extenso corta com reticências em vez de quebrar a linha e estourar a altura fixa da linha.
          slotProps={{
            primary: { noWrap: true, sx: { fontSize: "0.8125rem" } },
            secondary: { noWrap: true, sx: { fontSize: "0.7rem" } },
          }}
          sx={{ minWidth: 0 }}
        />
        {disabledReason && <Block fontSize="small" color="disabled" sx={{ mr: 1 }} />}
      </ListItemButton>
    </ListItem>
  );

  if (disabledReason) {
    return (
      <Tooltip title={disabledReason} placement="right">
        <span style={style}>{row}</span>
      </Tooltip>
    );
  }
  return <div style={style}>{row}</div>;
}
const TransferListRow = memo(TransferListRowInner) as typeof TransferListRowInner;

export function TransferList<T>({
  items,
  selectedIds,
  onChange,
  getId,
  getLabel,
  getSecondaryLabel,
  getDisabledReason,
  leftTitle = "Disponíveis",
  rightTitle = "Vinculados",
  itemNoun = "item(ns)",
  searchPlaceholder = "Filtrar...",
  hideSearch = false,
  onMoveRight,
  onMoveLeft,
  renderRightExtra,
}: TransferListProps<T>) {
  const [checked, setChecked] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const checkedSet = useMemo(() => new Set(checked), [checked]);

  // Marcar/desmarcar um checkbox só muda `checked` (estado local, não afeta
  // items/selectedIds/searchQuery) — memoizar aqui evita reordenar e
  // refiltrar as duas colunas inteiras a cada clique.
  const left = useMemo(
    () => [...items.filter((i) => !selectedIds.includes(getId(i)))].sort((a, b) => getLabel(a).localeCompare(getLabel(b))),
    [items, selectedIds, getId, getLabel],
  );
  const right = useMemo(
    () => [...items.filter((i) => selectedIds.includes(getId(i)))].sort((a, b) => getLabel(a).localeCompare(getLabel(b))),
    [items, selectedIds, getId, getLabel],
  );
  const leftVisible = useMemo(
    () => left.filter((i) => getLabel(i).toLowerCase().includes(searchQuery)),
    [left, searchQuery, getLabel],
  );
  const rightVisible = useMemo(
    () => right.filter((i) => getLabel(i).toLowerCase().includes(searchQuery)),
    [right, searchQuery, getLabel],
  );

  const leftMovable = left.filter((i) => !getDisabledReason?.(i)).map(getId);

  const leftIds = left.map(getId);
  const rightIds = right.map(getId);
  const leftChecked = checked.filter((id) => leftIds.includes(id));
  const rightChecked = checked.filter((id) => rightIds.includes(id));

  // Referência estável entre renders: ListItemButton já fica disabled (via
  // disabledReason) e bloqueia o clique antes de chegar aqui, então não
  // precisa reabrir esse cálculo a cada toggle — isso é o que deixa
  // TransferListRow memoizável de verdade.
  const handleToggle = useCallback((id: string) => {
    setChecked((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }, []);

  const handleMoveRight = () => {
    onChange([...selectedIds, ...leftChecked]);
    setChecked((prev) => prev.filter((id) => !leftChecked.includes(id)));
    onMoveRight?.();
  };

  const handleMoveAllRight = () => {
    if (leftMovable.length === 0) return;
    onChange([...selectedIds, ...leftMovable]);
    setChecked((prev) => prev.filter((id) => !leftMovable.includes(id)));
    onMoveRight?.();
  };

  const handleMoveLeft = () => {
    onChange(selectedIds.filter((id) => !rightChecked.includes(id)));
    setChecked((prev) => prev.filter((id) => !rightChecked.includes(id)));
    onMoveLeft?.();
  };

  const handleMoveAllLeft = () => {
    if (rightIds.length === 0) return;
    onChange(selectedIds.filter((id) => !rightIds.includes(id)));
    setChecked((prev) => prev.filter((id) => !rightIds.includes(id)));
    onMoveLeft?.();
  };

  const customList = (title: string, visiveis: T[], isRight: boolean) => {
    return (
      <Box sx={{ height: HEADER_HEIGHT + LIST_AREA_HEIGHT }}>
        <Box sx={{ height: HEADER_HEIGHT, px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.subtle", boxSizing: "border-box" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.8 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {visiveis.length} {itemNoun}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: "background.paper" }}>
          <VirtualList<TransferRowProps<T>>
            rowComponent={TransferListRow}
            rowCount={visiveis.length}
            rowHeight={ROW_HEIGHT}
            rowProps={{
              visiveis,
              isRight,
              checkedSet,
              getId,
              getLabel,
              getSecondaryLabel,
              getDisabledReason,
              renderRightExtra,
              onToggle: handleToggle,
            }}
            style={{ height: LIST_AREA_HEIGHT }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!hideSearch && (
        <TextField
          size="small"
          fullWidth
          placeholder={searchPlaceholder}
          onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          sx={{ mb: 2 }}
        />
      )}
      <Grid
        container
        spacing={0}
        alignItems="stretch"
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
      >
        <Grid size="grow">{customList(leftTitle, leftVisible, false)}</Grid>

        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            borderLeft: "1px solid",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.subtle",
            minWidth: 56,
          }}
        >
          {/* aria-label duplicado entre a seta dupla e a seta simples de cada
              lado é herdado de CompanyTransferList de propósito — UserPage's
              moveCompany (src/pages/user/__tests__/UserPage.test.tsx)
              distingue as duas por posição (getAllByRole(...)[0] é "mover
              tudo", [1] é "mover selecionados"), não por label; trocar o
              texto aqui quebraria esse teste sem tocar nele. */}
          <Button variant="contained" size="small" onClick={handleMoveAllRight} disabled={leftMovable.length === 0} aria-label="move selected right" sx={{ minWidth: 36, px: 1 }}>
            <KeyboardDoubleArrowRight />
          </Button>
          <Button variant="contained" size="small" onClick={handleMoveRight} disabled={leftChecked.length === 0} aria-label="move selected right" sx={{ minWidth: 36, px: 1 }}>
            <KeyboardArrowRight />
          </Button>
          <Button variant="contained" size="small" onClick={handleMoveLeft} disabled={rightChecked.length === 0} aria-label="move selected left" sx={{ minWidth: 36, px: 1 }}>
            <KeyboardArrowLeft />
          </Button>
          <Button variant="contained" size="small" onClick={handleMoveAllLeft} disabled={rightIds.length === 0} aria-label="move selected left" sx={{ minWidth: 36, px: 1 }}>
            <KeyboardDoubleArrowLeft />
          </Button>
        </Grid>

        <Grid size="grow">{customList(rightTitle, rightVisible, true)}</Grid>
      </Grid>
    </Box>
  );
}
